import { Button, Label } from '@kcftech/design-system';
import { DatePicker } from 'components/date_time/DatePicker';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { addMonths, isAfter } from 'date-fns';
import { useState } from 'react';
import { useController, useFormContext, useFormState } from 'react-hook-form';
import { AlarmThresholdFormFields } from './shared';
import { StyledCard } from './styled';

function validateSamplingWindow(
    startValue: Date | null | undefined,
    endValue: Date | null | undefined,
) {
    if (!startValue || !endValue) {
        return undefined;
    }

    const today = new Date();
    if (isAfter(startValue, today) || isAfter(endValue, today)) {
        return FUTURE_DATE_ERROR;
    }

    if (startValue > endValue) {
        return START_BEFORE_END_ERROR;
    }

    if (isAfter(endValue, addMonths(startValue, MAX_SAMPLING_WINDOW_MONTHS))) {
        return MAX_WINDOW_ERROR;
    }

    return undefined;
}

type SamplingRange = 'week1' | 'week2' | 'month1' | 'custom';

const MAX_SAMPLING_WINDOW_MONTHS = 6;
const START_BEFORE_END_ERROR = 'Start date must be before end date.';
const MAX_WINDOW_ERROR = 'Sampling window cannot exceed 6 months.';
const FUTURE_DATE_ERROR = 'Future dates are not allowed.';

export function SamplingCard() {
    const { control, getValues } = useFormContext<AlarmThresholdFormFields>();

    const { errors } = useFormState({ control });

    const startField = useController({
        control,
        name: 'start',
        rules: {
            deps: ['end'],
            validate: (startValue) => {
                const endValue = getValues('end');

                if (!startValue) {
                    return 'Please enter a valid date.';
                }

                const rangeError = validateSamplingWindow(startValue, endValue);
                if (rangeError) {
                    return rangeError;
                }

                return undefined;
            },
        },
    }).field;
    const endField = useController({
        control,
        name: 'end',
        rules: {
            deps: ['start'],
            validate: (endValue) => {
                const startValue = getValues('start');

                if (!endValue) {
                    return 'Please enter a valid date.';
                }

                const rangeError = validateSamplingWindow(startValue, endValue);
                if (rangeError) {
                    return rangeError;
                }

                return undefined;
            },
        },
    }).field;

    const handleStartDateChange = (value: Date | null) => {
        startField.onChange(value);
        setSelectedRange('custom');
    };

    const handleEndDateChange = (value: Date | null) => {
        endField.onChange(value);
        setSelectedRange('custom');
    };

    const [selectedRange, setSelectedRange] = useState<SamplingRange>('custom');

    const today = new Date();
    const startMin = endField.value
        ? addMonths(endField.value, -MAX_SAMPLING_WINDOW_MONTHS)
        : undefined;
    const startMax = endField.value && endField.value < today ? endField.value : today;
    const endMin = startField.value ?? undefined;
    const endMaxFromStart = startField.value
        ? addMonths(startField.value, MAX_SAMPLING_WINDOW_MONTHS)
        : today;
    const endMax = endMaxFromStart > today ? today : endMaxFromStart;

    const setRange = (days: number, range: SamplingRange) => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - days);

        startField.onChange(start);
        endField.onChange(end);
        setSelectedRange(range);
    };

    return (
        <StyledCard className="flex-column gap-10">
            <Label required label="Sampling Window" />
            <div className="flex gap-10">
                <Button
                    type="button"
                    appearance={selectedRange === 'week1' ? 'primary' : 'secondary'}
                    spacing="compact"
                    onClick={() => setRange(7, 'week1')}
                >
                    1 Week
                </Button>
                <Button
                    type="button"
                    appearance={selectedRange === 'week2' ? 'primary' : 'secondary'}
                    spacing="compact"
                    onClick={() => setRange(14, 'week2')}
                >
                    2 Weeks
                </Button>
                <Button
                    type="button"
                    appearance={selectedRange === 'month1' ? 'primary' : 'secondary'}
                    spacing="compact"
                    onClick={() => setRange(30, 'month1')}
                >
                    1 Month (30 days)
                </Button>
                <Button
                    type="button"
                    appearance={selectedRange === 'custom' ? 'primary' : 'secondary'}
                    spacing="compact"
                    onClick={() => setSelectedRange('custom')}
                >
                    Custom
                </Button>
            </div>
            <div className="flex gap-10">
                <ValidatableLabel
                    label="Start Date"
                    required
                    error={errors.start}
                    className="flex-1"
                >
                    <DatePicker
                        value={startField.value}
                        min={startMin}
                        max={startMax}
                        onChange={(e) => {
                            handleStartDateChange(e.value);
                        }}
                        onBlur={startField.onBlur}
                    />
                </ValidatableLabel>
                <ValidatableLabel
                    label="End Date"
                    required
                    error={errors.end}
                    className="flex-1"
                >
                    <DatePicker
                        value={endField.value}
                        min={endMin}
                        max={endMax}
                        onChange={(e) => {
                            handleEndDateChange(e.value);
                        }}
                        onBlur={endField.onBlur}
                    />
                </ValidatableLabel>
            </div>
        </StyledCard>
    );
}
